/**
 * Tarjeta de cuenta en el listado del login.
 *
 * - `primary`: card alta con icono passkey/fingerprint y el label biométrico
 *   debajo del nombre (ej. "Face ID · este dispositivo"). Sin botón ×.
 * - `secondary` (default): card más compacta con avatar de iniciales, email y
 *   rol; el botón × aparece en hover y permite quitarla del dispositivo.
 */
import type { RecentAccount } from '@api/types';
import * as Icons from '@components/Icons';

const ROLE_LABEL: Record<string, string> = {
  superadmin: 'Superadmin',
  center_admin: 'Center manager',
  staff: 'Staff',
};

function initials(name: string | null, fallback: string): string {
  const trimmed = name?.trim();
  const source = trimmed && trimmed.length > 0 ? trimmed : fallback;
  return source
    .split(/\s+/)
    .map((p) => p.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function tone(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return Math.abs(h) % 5;
}

interface RecentAccountCardProps {
  account: RecentAccount;
  variant: 'primary' | 'secondary';
  /** Solo para primary. Copy: "Face ID · este dispositivo", etc. */
  biometricLabel?: string;
  onClick: () => void;
  /** Botón × para quitar la cuenta del listado (solo secondary). */
  onRemove?: () => void;
  disabled?: boolean;
}

export function RecentAccountCard({
  account,
  variant,
  biometricLabel,
  onClick,
  onRemove,
  disabled,
}: RecentAccountCardProps) {
  const trimmedName = account.name?.trim();
  const displayName = trimmedName && trimmedName.length > 0 ? trimmedName : account.email;

  if (variant === 'primary') {
    return (
      <button
        type="button"
        className="account-card is-primary"
        onClick={onClick}
        disabled={disabled}
        data-testid="account-card-primary"
      >
        <span className="account-card-icon" aria-hidden>
          <Icons.Fingerprint size={22} />
        </span>
        <span className="account-card-body">
          <span className="account-card-title">Continuar como {displayName}</span>
          {biometricLabel && (
            <span className="account-card-sub">{biometricLabel}</span>
          )}
        </span>
        <Icons.ChevR size={16} className="account-card-chev" />
      </button>
    );
  }

  return (
    <div className="account-card is-secondary" data-testid="account-card-secondary">
      <button
        type="button"
        className="account-card-main"
        onClick={onClick}
        disabled={disabled}
      >
        <span className="avatar" data-tone={tone(account.email)} aria-hidden>
          {initials(account.name, account.email)}
        </span>
        <span className="account-card-body">
          <span className="account-card-title">{displayName}</span>
          <span className="account-card-sub">
            {account.email} · {ROLE_LABEL[account.role] ?? account.role}
          </span>
        </span>
        <Icons.ChevR size={16} className="account-card-chev" />
      </button>
      {onRemove && (
        <button
          type="button"
          className="account-card-remove"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          aria-label={`Quitar ${displayName} de este dispositivo`}
          title="Quitar de este dispositivo"
        >
          <Icons.X size={12} />
        </button>
      )}
    </div>
  );
}
